import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';

const DealEntryModal = ({ isOpen, onClose, onSave, clients, deal, onOpenClientModal, onOpenSupplierModal, suppliers }) => {
  const [formData, setFormData] = useState({
    clientId: '',
    title: '',
    value: '',
    commission: '',
    saleDate: new Date()?.toISOString()?.split('T')?.[0],
    destination: '',
    passengers: '1',
    notes: '',
    isPaid: false,
    bank: '',
    paymentDate: '',
    supplierId: '',
    // Invoice control fields
    hasReceivedInvoice: false,
    invoiceReceivedDate: '',
    invoicePaymentDate: '',
    taxInvoiceIssueDate: '',
    invoiceFile: null,
    invoiceFileName: '',
    taxInvoiceFile: null,
    taxInvoiceFileName: '',
    // Supplier data fields
    supplierName: '',
    supplierContact: '',
    supplierEmail: '',
    supplierPaymentTerms: ''
  });

  useEffect(() => {
    if (deal && isOpen) {
      // Populate form with deal data for editing
      setFormData({
        clientId: deal?.clientId?.toString() || '',
        title: deal?.title || '',
        value: deal?.value?.toString() || '',
        commission: deal?.commission?.toString() || '',
        saleDate: deal?.saleDate || new Date()?.toISOString()?.split('T')?.[0],
        destination: deal?.destination || '',
        passengers: deal?.passengers?.toString() || '1',
        notes: deal?.notes || '',
        isPaid: deal?.isPaid || false,
        bank: deal?.bank || '',
        paymentDate: deal?.paymentDate || '',
        supplierId: deal?.supplierId?.toString() || '',
        // Invoice control fields
        hasReceivedInvoice: deal?.hasReceivedInvoice || false,
        invoiceReceivedDate: deal?.invoiceReceivedDate || '',
        invoicePaymentDate: deal?.invoicePaymentDate || '',
        taxInvoiceIssueDate: deal?.taxInvoiceIssueDate || '',
        invoiceFile: deal?.invoiceFile || null,
        invoiceFileName: deal?.invoiceFileName || '',
        taxInvoiceFile: deal?.taxInvoiceFile || null,
        taxInvoiceFileName: deal?.taxInvoiceFileName || '',
        // Supplier data fields
        supplierName: deal?.supplierName || '',
        supplierContact: deal?.supplierContact || '',
        supplierEmail: deal?.supplierEmail || '',
        supplierPaymentTerms: deal?.supplierPaymentTerms || ''
      });
      
      // Recalculate financials when editing
      calculateFinancials({
        value: deal?.value || '',
        commission: deal?.commission || ''
      });
    } else if (!deal && isOpen) {
      // Reset form for new deal
      setFormData({
        clientId: '',
        title: '',
        value: '',
        commission: '',
        saleDate: new Date()?.toISOString()?.split('T')?.[0],
        destination: '',
        passengers: '1',
        notes: '',
        isPaid: false,
        bank: '',
        paymentDate: '',
        supplierId: '',
        hasReceivedInvoice: false,
        invoiceReceivedDate: '',
        invoicePaymentDate: '',
        taxInvoiceIssueDate: '',
        invoiceFile: null,
        invoiceFileName: '',
        taxInvoiceFile: null,
        taxInvoiceFileName: '',
        supplierName: '',
        supplierContact: '',
        supplierEmail: '',
        supplierPaymentTerms: ''
      });
      setCalculatedValues({
        revenue: 0,
        profitWithoutDiscount: 0,
        profitWith6Discount: 0
      });
    }
  }, [deal, isOpen]);

  const [errors, setErrors] = useState({});
  const [isNewClient, setIsNewClient] = useState(false);
  const [isNewSupplier, setIsNewSupplier] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({
    revenue: 0,
    profitWithoutDiscount: 0,
    profitWith6Discount: 0
  });

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }

    // Recalculate financial values when relevant fields change
    if (field === 'value' || field === 'commission') {
      calculateFinancials({
        ...formData,
        [field]: value
      });
    }
  };

  const calculateFinancials = (data) => {
    const value = parseFloat(data?.value) || 0;
    const commission = parseFloat(data?.commission) || 0;

    const revenue = value - commission;
    const profitWithoutDiscount = revenue;
    const profitWith6Discount = revenue * 0.94; // 6% reduction

    setCalculatedValues({
      revenue: revenue?.toFixed(2),
      profitWithoutDiscount: profitWithoutDiscount?.toFixed(2),
      profitWith6Discount: profitWith6Discount?.toFixed(2)
    });
  };

  const handleClientChange = (e) => {
    const clientId = e?.target?.value;
    
    if (clientId === 'new') {
      onOpenClientModal?.();
    } else {
      setIsNewClient(false);
      setFormData(prev => ({
        ...prev,
        clientId: clientId
      }));
    }
  };

  const handleSupplierChange = (e) => {
    const supplierId = e?.target?.value;
    
    if (supplierId === 'new') {
      onOpenSupplierModal?.();
    } else {
      setFormData(prev => ({
        ...prev,
        supplierId: supplierId
      }));
    }
  };

  const handleFileChange = (field, fileNameField, event) => {
    const file = event?.target?.files?.[0];
    if (file) {
      setFormData(prev => ({
        ...prev,
        [field]: file,
        [fileNameField]: file?.name
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Client validation
    if (!formData?.clientId) {
      newErrors.clientId = 'Selecione um cliente';
    }

    // Title validation
    if (!formData?.title?.trim()) {
      newErrors.title = 'Título é obrigatório';
    }

    // Value validation
    if (!formData?.value || parseFloat(formData?.value) <= 0) {
      newErrors.value = 'Valor do negócio deve ser maior que zero';
    }

    // Commission validation (now represents supplier payment)
    if (!formData?.commission || parseFloat(formData?.commission) <= 0) {
      newErrors.commission = 'Valor pago ao fornecedor deve ser maior que zero';
    }

    // Date validation
    if (!formData?.saleDate) {
      newErrors.saleDate = 'Data da venda é obrigatória';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      const dealData = {
        clientId: formData?.clientId,
        title: formData?.title,
        value: parseFloat(formData?.value),
        commission: parseFloat(formData?.commission),
        saleDate: formData?.saleDate,
        destination: formData?.destination,
        passengers: formData?.passengers,
        notes: formData?.notes,
        isPaid: formData?.isPaid,
        bank: formData?.bank,
        paymentDate: formData?.paymentDate,
        supplierId: formData?.supplierId,
        // Invoice control fields
        hasReceivedInvoice: formData?.hasReceivedInvoice,
        invoiceReceivedDate: formData?.invoiceReceivedDate,
        invoicePaymentDate: formData?.invoicePaymentDate,
        taxInvoiceIssueDate: formData?.taxInvoiceIssueDate,
        invoiceFile: formData?.invoiceFile,
        invoiceFileName: formData?.invoiceFileName,
        taxInvoiceFile: formData?.taxInvoiceFile,
        taxInvoiceFileName: formData?.taxInvoiceFileName,
        // Supplier data fields
        supplierName: formData?.supplierName,
        supplierContact: formData?.supplierContact,
        supplierEmail: formData?.supplierEmail,
        supplierPaymentTerms: formData?.supplierPaymentTerms
      };

      onSave(dealData);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      clientId: '',
      title: '',
      value: '',
      commission: '',
      saleDate: new Date()?.toISOString()?.split('T')?.[0],
      destination: '',
      passengers: '1',
      notes: '',
      isPaid: false,
      bank: '',
      paymentDate: '',
      supplierId: '',
      // Invoice control fields
      hasReceivedInvoice: false,
      invoiceReceivedDate: '',
      invoicePaymentDate: '',
      taxInvoiceIssueDate: '',
      invoiceFile: null,
      invoiceFileName: '',
      taxInvoiceFile: null,
      taxInvoiceFileName: '',
      // Supplier data fields
      supplierName: '',
      supplierContact: '',
      supplierEmail: '',
      supplierPaymentTerms: ''
    });
    setErrors({});
    setIsNewClient(false);
    setIsNewSupplier(false);
    setCalculatedValues({
      revenue: 0,
      profitWithoutDiscount: 0,
      profitWith6Discount: 0
    });
    onClose();
  };

  return (
    <>
      {isOpen && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-lg border border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
              <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
                <Icon name="TrendingUp" size={24} />
                {deal ? 'Editar Negociação' : 'Nova Negociação/Entrada'}
              </h2>
              <button
                onClick={handleClose}
                className="p-2 hover:bg-muted rounded-md transition-colors"
              >
                <Icon name="X" size={20} />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="space-y-6">
                {/* Cliente Section */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                      <Icon name="User" size={16} />
                      Informações do Cliente
                    </h3>
                    <button
                      type="button"
                      onClick={() => onOpenClientModal?.()}
                      className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors"
                    >
                      <Icon name="UserPlus" size={14} />
                      Cadastrar Novo Cliente
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Selecionar Cliente *
                      </label>
                      <Select
                        name="clientId"
                        value={formData?.clientId}
                        onChange={handleClientChange}
                        options={[
                          ...clients?.map(client => ({
                            value: client?.id?.toString(),
                            label: `${client?.name} - ${client?.email || 'Sem email'}`
                          })),
                          { value: 'new', label: '+ Cadastrar Novo Cliente' }
                        ]}
                        placeholder="Selecione um cliente"
                        error={errors?.clientId}
                        searchable={true}
                      />
                      {errors?.clientId && (
                        <p className="text-error text-xs mt-1">{errors?.clientId}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Title and Value Section */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Package" size={16} />
                    Título e Valores
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Título *
                      </label>
                      <Input
                        type="text"
                        value={formData?.title}
                        onChange={(e) => handleChange('title', e?.target?.value)}
                        placeholder="Título do negócio"
                        className={errors?.title ? 'border-error' : ''}
                      />
                      {errors?.title && (
                        <p className="text-error text-xs mt-1">{errors?.title}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Valor do Negócio (R$) *
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData?.value}
                        onChange={(e) => handleChange('value', e?.target?.value)}
                        placeholder="0.00"
                        className={errors?.value ? 'border-error' : ''}
                      />
                      {errors?.value && (
                        <p className="text-error text-xs mt-1">{errors?.value}</p>
                      )}
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Valor Pago ao Fornecedor (R$) *
                      </label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData?.commission}
                        onChange={(e) => handleChange('commission', e?.target?.value)}
                        placeholder="0.00"
                        className={errors?.commission ? 'border-error' : ''}
                      />
                      {errors?.commission && (
                        <p className="text-error text-xs mt-1">{errors?.commission}</p>
                      )}
                    </div>

                    {/* Financial Calculations Display */}
                    <div className="col-span-2 bg-muted/30 rounded-lg p-4 space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Valor da Receita:</span>
                        <span className="text-sm font-semibold text-foreground">
                          R$ {calculatedValues?.revenue}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Lucro (sem redução):</span>
                        <span className="text-sm font-semibold text-success">
                          R$ {calculatedValues?.profitWithoutDiscount}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-muted-foreground">Lucro (com redução de 6%):</span>
                        <span className="text-sm font-semibold text-primary">
                          R$ {calculatedValues?.profitWith6Discount}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Date Fields Section */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Calendar" size={16} />
                    Data da Venda
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Data da Venda *
                      </label>
                      <Input
                        type="date"
                        value={formData?.saleDate}
                        onChange={(e) => handleChange('saleDate', e?.target?.value)}
                        className={errors?.saleDate ? 'border-error' : ''}
                      />
                      {errors?.saleDate && (
                        <p className="text-error text-xs mt-1">{errors?.saleDate}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Payment Status Section */}
                <div className="border-t border-border pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-foreground mb-3">Status de Pagamento</h3>
                  
                  <div className="space-y-3">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="isPaid"
                        checked={formData?.isPaid}
                        onChange={(e) => setFormData({ ...formData, isPaid: e?.target?.checked })}
                        className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                      />
                      <label htmlFor="isPaid" className="ml-2 text-sm text-foreground">
                        Negócio já foi pago
                      </label>
                    </div>

                    {formData?.isPaid && (
                      <>
                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Fornecedor
                          </label>
                          <Select
                            name="supplierId"
                            value={formData?.supplierId}
                            onChange={handleSupplierChange}
                            options={[
                              { value: '', label: 'Selecione o fornecedor (opcional)' },
                              ...suppliers?.map(supplier => ({
                                value: supplier?.id?.toString(),
                                label: supplier?.name
                              })),
                              { value: 'new', label: '+ Cadastrar Novo Fornecedor' }
                            ]}
                            placeholder="Selecione o fornecedor"
                            searchable={true}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Banco de Recebimento *
                          </label>
                          <select
                            value={formData?.bank}
                            onChange={(e) => setFormData({ ...formData, bank: e?.target?.value })}
                            required={formData?.isPaid}
                            className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          >
                            <option value="">Selecione o banco</option>
                            <option value="inter">Banco INTER</option>
                            <option value="mercadoPago">Mercado Pago</option>
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-foreground mb-2">
                            Data de Recebimento *
                          </label>
                          <input
                            type="date"
                            value={formData?.paymentDate}
                            onChange={(e) => setFormData({ ...formData, paymentDate: e?.target?.value })}
                            required={formData?.isPaid}
                            className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* Invoice Control Section */}
                <div className="border-t border-border pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="FileText" size={16} />
                    Controle de Fatura
                  </h3>
                  
                  <div className="space-y-4">
                    {/* Received Invoice Checkbox */}
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        id="hasReceivedInvoice"
                        checked={formData?.hasReceivedInvoice}
                        onChange={(e) => setFormData({ ...formData, hasReceivedInvoice: e?.target?.checked })}
                        className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary"
                      />
                      <label htmlFor="hasReceivedInvoice" className="ml-2 text-sm text-foreground">
                        Recebeu Fatura?
                      </label>
                    </div>

                    {/* Invoice Received Date - Shows when checkbox is checked */}
                    {formData?.hasReceivedInvoice && (
                      <div>
                        <label className="block text-sm font-medium text-foreground mb-2">
                          Data do Recebimento da Fatura *
                        </label>
                        <Input
                          type="date"
                          value={formData?.invoiceReceivedDate}
                          onChange={(e) => handleChange('invoiceReceivedDate', e?.target?.value)}
                          className="w-full"
                        />
                      </div>
                    )}

                    {/* Invoice Payment Date */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Data do Pagamento da Fatura
                      </label>
                      <Input
                        type="date"
                        value={formData?.invoicePaymentDate}
                        onChange={(e) => handleChange('invoicePaymentDate', e?.target?.value)}
                        className="w-full"
                      />
                    </div>

                    {/* Tax Invoice Issue Date */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Data para Emissão da Nota Fiscal
                      </label>
                      <Input
                        type="date"
                        value={formData?.taxInvoiceIssueDate}
                        onChange={(e) => handleChange('taxInvoiceIssueDate', e?.target?.value)}
                        className="w-full"
                      />
                    </div>

                    {/* Invoice File Upload */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Anexar Fatura
                      </label>
                      <div className="flex items-center gap-3">
                        <label className="flex-1 cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-2.5 bg-background border border-border rounded-lg hover:bg-muted transition-colors">
                            <Icon name="Upload" size={16} className="text-muted-foreground" />
                            <span className="text-sm text-foreground">
                              {formData?.invoiceFileName || 'Selecionar arquivo'}
                            </span>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange('invoiceFile', 'invoiceFileName', e)}
                            className="hidden"
                          />
                        </label>
                        {formData?.invoiceFileName && (
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, invoiceFile: null, invoiceFileName: '' })}
                            className="p-2 text-error hover:bg-error/10 rounded-md transition-colors"
                          >
                            <Icon name="X" size={16} />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Formatos aceitos: PDF, JPG, PNG
                      </p>
                    </div>

                    {/* Tax Invoice File Upload */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Anexar Nota Fiscal
                      </label>
                      <div className="flex items-center gap-3">
                        <label className="flex-1 cursor-pointer">
                          <div className="flex items-center gap-2 px-4 py-2.5 bg-background border border-border rounded-lg hover:bg-muted transition-colors">
                            <Icon name="Upload" size={16} className="text-muted-foreground" />
                            <span className="text-sm text-foreground">
                              {formData?.taxInvoiceFileName || 'Selecionar arquivo'}
                            </span>
                          </div>
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleFileChange('taxInvoiceFile', 'taxInvoiceFileName', e)}
                            className="hidden"
                          />
                        </label>
                        {formData?.taxInvoiceFileName && (
                          <button
                            type="button"
                            onClick={() => setFormData({ ...formData, taxInvoiceFile: null, taxInvoiceFileName: '' })}
                            className="p-2 text-error hover:bg-error/10 rounded-md transition-colors"
                          >
                            <Icon name="X" size={16} />
                          </button>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        Formatos aceitos: PDF, JPG, PNG
                      </p>
                    </div>
                  </div>
                </div>

                {/* Supplier Data Section */}
                <div className="border-t border-border pt-4 mt-4">
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Building" size={16} />
                    Dados do Fornecedor
                  </h3>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Nome do Fornecedor
                      </label>
                      <Input
                        type="text"
                        value={formData?.supplierName}
                        onChange={(e) => handleChange('supplierName', e?.target?.value)}
                        placeholder="Nome completo do fornecedor"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Telefone de Contato
                      </label>
                      <Input
                        type="tel"
                        value={formData?.supplierContact}
                        onChange={(e) => handleChange('supplierContact', e?.target?.value)}
                        placeholder="(00) 00000-0000"
                        className="w-full"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        E-mail do Fornecedor
                      </label>
                      <Input
                        type="email"
                        value={formData?.supplierEmail}
                        onChange={(e) => handleChange('supplierEmail', e?.target?.value)}
                        placeholder="fornecedor@email.com"
                        className="w-full"
                      />
                    </div>

                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Condições de Pagamento
                      </label>
                      <textarea
                        value={formData?.supplierPaymentTerms}
                        onChange={(e) => handleChange('supplierPaymentTerms', e?.target?.value)}
                        placeholder="Ex: Pagamento em 30 dias, 50% na entrada..."
                        className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        rows="2"
                      />
                    </div>
                  </div>
                </div>

                {/* Destination Section */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="MapPin" size={16} />
                    Destino
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Destino
                      </label>
                      <Input
                        type="text"
                        value={formData?.destination}
                        onChange={(e) => handleChange('destination', e?.target?.value)}
                        placeholder="Endereço ou localização"
                      />
                    </div>
                  </div>
                </div>

                {/* Passengers Section */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Users" size={16} />
                    Passageiros
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Número de Passageiros
                      </label>
                      <Input
                        type="number"
                        min="1"
                        value={formData?.passengers}
                        onChange={(e) => handleChange('passengers', e?.target?.value)}
                        placeholder="1"
                      />
                    </div>
                  </div>
                </div>

                {/* Notes Section */}
                <div>
                  <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                    <Icon name="Note" size={16} />
                    Observações
                  </h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="col-span-2">
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Observações
                      </label>
                      <textarea
                        value={formData?.notes}
                        onChange={(e) => handleChange('notes', e?.target?.value)}
                        placeholder="Adicione observações importantes"
                        className="w-full px-4 py-2.5 bg-background border border-border rounded-lg text-foreground focus:outline-none focus:ring-2 focus:ring-primary"
                        rows="3"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </form>

            <div className="sticky bottom-0 bg-card border-t border-border p-4 flex items-center justify-end gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="default"
                iconName="Check"
                iconPosition="left"
                onClick={handleSubmit}
              >
                {deal ? 'Salvar Alterações' : 'Registrar Negociação'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default DealEntryModal;